import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import mapUtils from "../../../utils/map";
import { GeometryType } from "./GeometryTypeSelect";
import AlterType from "./AlterType";
import { useMap } from "../../../App";
import ol from "openlayers";
import { InventoryGeometryItem } from "../../../hooks/database/types/inventories";
import { useMapProjection } from "../../../hooks/useMapView";
import { useBaseGeometryController } from "./useBaseGeometryController";

function transformCoordinates(inCoords: ol.Coordinate[], currViewProj: ol.ProjectionLike) {
  return inCoords.map((coord) => ol.proj.transform(coord, "EPSG:4326", currViewProj));
}

function makeFeature(coords: ol.Coordinate[], geometryType: Exclude<GeometryType, "Point">) {
  return new ol.Feature(geometryType === "LineString" ? new ol.geom.LineString(coords) : new ol.geom.Polygon([coords]));
}

const ModifyGeometry: FC<{
  coordinates: ol.Coordinate[];
  geomType: Exclude<GeometryType, "Point">;
  onCancel: () => void;
  onSave: (geojson: InventoryGeometryItem["geom"]) => void;
}> = (props) => {
  const map = useMap()!;
  const projection = useMapProjection();

  const { drawSource, saveGeometry } = useBaseGeometryController(props.onSave);

  const [isAltered, setIsAltered] = useState(false);
  const [changedFeature] = useState(makeFeature(transformCoordinates(props.coordinates as any, projection), props.geomType));
  useEffect(() => {
    drawSource.addFeature(changedFeature);
    const key = changedFeature.on("change", () => {
      setIsAltered(true);
    });
    return () => {
      drawSource.removeFeature(changedFeature);
      ol.Observable.unByKey(key);
    };
  }, [drawSource, changedFeature]);

  const [alterType, setAlterType] = useState<"move" | "rotate" | "edit">("move");

  const makeEditInteraction = useCallback(() => {
    return new ol.interaction.Modify({
      features: new ol.Collection<ol.Feature>([changedFeature]),
    });
  }, [changedFeature]);

  const makeMoveInteraction = useCallback(() => {
    return new ol.interaction.Translate({
      features: drawSource.getFeaturesCollection(),
    });
  }, [drawSource]);

  const makeRotateInteraction = useCallback(() => {
    let ftCenter: ol.Coordinate | null = null;
    let rotateDownStart: ol.Coordinate | null = null;
    let ftRotateLine: ol.Feature | null = null;
    let startCoordinates: ol.Coordinate[] | ol.Coordinate[][] | null = null;
    let baseAngle: number | null = null;
    let dragCursor = "pointer";
    let dragPrevCursor: string | null = null;

    return new ol.interaction.Pointer({
      handleDownEvent(event: ol.MapBrowserPointerEvent) {
        rotateDownStart = event.coordinate!;
        ftCenter = mapUtils.getFeatureExtentCenter(changedFeature);
        startCoordinates = (changedFeature.getGeometry() as any).getCoordinates();
        return true;
      },
      handleDragEvent(event: ol.MapBrowserPointerEvent) {
        if (!rotateDownStart || !ftCenter) {
          return false;
        }
        if (!ftRotateLine) {
          ftRotateLine = new ol.Feature();
          ftRotateLine.setStyle(
            new ol.style.Style({
              fill: new ol.style.Fill({
                color: "rgba(10, 20, 20, 0.7)",
              }),
              stroke: new ol.style.Stroke({
                color: "rgba(10, 20, 20, 0.7)",
                width: 4,
              }),
            })
          );
          drawSource.addFeature(ftRotateLine);
        }
        ftRotateLine.setGeometry(mapUtils.createLineString(ftCenter, event.coordinate));
        if (!baseAngle) {
          const dX = rotateDownStart[0] - ftCenter[0];
          const dY = ftCenter[1] - rotateDownStart[1];
          baseAngle = Math.atan2(dY, dX) * (180 / Math.PI);
        }
        const deltaX = event.coordinate[0] - ftCenter[0];
        const deltaY = ftCenter[1] - event.coordinate[1];
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

        let newCoordinates = null;
        const geometry = changedFeature.getGeometry();
        if (geometry.getType() === "LineString") {
          newCoordinates = mapUtils.rotateCoordinates(startCoordinates as ol.Coordinate[], ftCenter, baseAngle - angle);
        } else if (geometry.getType() === "Polygon") {
          newCoordinates = mapUtils.rotateArrayCoordinates(startCoordinates as ol.Coordinate[][], ftCenter, baseAngle - angle);
        }
        (geometry as any).setCoordinates(newCoordinates);
        changedFeature.setGeometry(geometry);
        return true;
      },
      handleMoveEvent(event: ol.MapBrowserPointerEvent) {
        if (dragCursor) {
          const map = event.map;
          const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => {
            return feature;
          });
          const element = event.map.getTargetElement() as HTMLDivElement;
          if (feature) {
            if (element.style.cursor !== dragCursor) {
              dragPrevCursor = element.style.cursor;
              element.style.cursor = dragCursor;
            }
          } else if (dragPrevCursor !== undefined) {
            element.style.cursor = dragPrevCursor as string;
            dragPrevCursor = null;
          }
        }
        return true;
      },
      handleUpEvent() {
        if (ftRotateLine) {
          drawSource.removeFeature(ftRotateLine);
        }
        baseAngle = null;
        ftRotateLine = null;
        ftCenter = null;
        startCoordinates = null;
        return false;
      },
    });
  }, [changedFeature, drawSource]);

  useEffect(() => {
    const interaction =
      alterType === "edit" ? makeEditInteraction() : alterType === "move" ? makeMoveInteraction() : makeRotateInteraction();
    map.addInteraction(interaction);
    return () => {
      map.removeInteraction(interaction);
    };
  }, [alterType, map, props.geomType, makeEditInteraction, makeMoveInteraction, makeRotateInteraction]);

  const handleConfirmClick = useCallback(() => {
    if (isAltered) {
      const geom = changedFeature.getGeometry();
      const coords =
        props.geomType === "LineString" ? (geom as ol.geom.LineString).getCoordinates() : (geom as ol.geom.Polygon).getCoordinates()[0];
      if (coords.length > 0) {
        saveGeometry(changedFeature.getGeometry());
      }

      setIsAltered(false);
    } else {
      props.onCancel();
    }
  }, [changedFeature, isAltered, props, saveGeometry]);

  const transformedStartPoint = useMemo(
    () => ol.proj.transform(ol.extent.getCenter(ol.extent.boundingExtent(props.coordinates)), "EPSG:4326", projection),
    [props.coordinates, projection]
  );
  useEffect(() => {
    if (transformedStartPoint) {
      mapUtils.panToCoord(map, transformedStartPoint);
    }
  }, [map, transformedStartPoint]);

  return (
    <>
      <AlterType value={alterType} onChange={setAlterType} />

      <button className="draw-confirm button button-fill" onClick={handleConfirmClick}>
        CONFIRM GEOMETRY
      </button>

      <button className="draw-cancel button button-fill" onClick={props.onCancel}>
        CANCEL
      </button>
    </>
  );
};

export default ModifyGeometry;
